import { Box, IconButton } from '@mui/material';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import { FeatureFlag, useFeatureFlag } from 'Utils/FeatureFlags';
import { useDispatch, useSelector } from 'react-redux';
import * as appActions from 'Actions/app';
import { useEffect, useState, useRef, useCallback } from 'react';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { PlayContainer } from 'Components/RoadmapComponents/PlayContainer';
import { Minimap } from 'Components/RoadmapComponents/Minimap';
import { Menu, MobileMenuButton } from 'Components/RoadmapComponents/Menu';
import { Path } from 'Components/RoadmapComponents/Path';
import { useNavigate, useLocation } from 'react-router-dom';
import { MainAppReducer, UserLevelData } from 'Types';
import { getHighestCompletedLevel, getUnitPctComplete } from 'Utils/UserProgress';
import { CustomCircularProgress } from 'Components/StyledComponents';
import Survey from './Survey';
import './Roadmap.css';
import useMediaQuery from '@mui/material/useMediaQuery';
import { isCapacitorNative } from 'Utils/Constants';
import { getDeviceClass } from 'Utils/Responsive';
import Confirmation from 'Containers/Payment/Confirmation';
import logger from 'Utils/Logger';
import { CopyErrorLogsButton } from 'Components/CopyErrorLogsButton';

// The narrow-desktop / small-window fallback that keeps the sheet UI on a very
// small non-phone viewport (e.g. a shrunk desktop browser window). This is a
// SIZE concern, distinct from the device-class check below which is the primary
// "this is a phone" signal - see useSheetUi.
const smallMobileRoadmapQuery = '(max-width:480px), (max-height:480px)';

// Dev environments only (never prod apps) — includes Capacitor dev builds so
// the copy-error-logs affordance is reachable on mobile.
const isDevEnvironment =
  process.env.REACT_APP_NODE_ENV === 'local' || process.env.REACT_APP_NODE_ENV === 'dev';

const Roadmap = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  // Debug Dummy MIDI: surface a floating entry point to the piano
  // calibration/demo scene when the experimental flag is enabled.
  const debugDummyMidiEnabled = useFeatureFlag(FeatureFlag.DEBUG_DUMMY_MIDI);

  const data = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const { levelData, levelSelect, userLevelData, tiersByLevels, unitSelect, userData } = data;

  // IMPORTANT: sane defaults (these are expected to be 0..1)
  const [areaToPreventScroll, setAreaToPreventScroll] = useState<string>('');
  const [scrollPos, setScrollPos] = useState<number>(0);
  const [scrollAreaSize, setScrollAreaSize] = useState<number>(0.5);

  const [pathScrollPos, setPathScrollPos] = useState(0);
  const roadmapRef = useRef<HTMLDivElement | null>(null);
  const latestLevelRef = useRef<HTMLDivElement | null>(null);

  const [cursorInRoadmap, setCursorInRoadmap] = useState(false);
  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryParam = params.get('show-thank-you');
    logger.info('queryParam:', queryParam);
    if (queryParam) setShowPaymentConfirmation(true);
  }, [location.search]);

  useEffect(() => {
    if (userData && userData.subscription_status == '') {
      navigate('/welcome');
    }
  }, [userData, navigate]);

  const [highestCompleteData, setHighestCompleteData] = useState(
    getHighestCompletedLevel(userLevelData),
  );
  useEffect(() => {
    setHighestCompleteData(getHighestCompletedLevel(userLevelData));
  }, [userLevelData]);

  // Which Play affordance the roadmap uses. The PRIMARY signal is device class:
  // phones get the bottom-sheet UI (LevelPreviewSheet) because it is designed for
  // the phone form factor - it superimposes over the roadmap and lets you swipe
  // between levels without a persistent Play bar eating vertical real estate.
  // Tablets and desktop get the persistent PlayContainer bar. A narrow non-phone
  // viewport (a shrunk desktop window <=480px) also falls back to the sheet so the
  // existing responsive web behavior is preserved.
  //
  // This replaces the previous purely viewport-size check. That check keyed only
  // on `(max-width:480px) OR (max-height:480px)`, so a phone in the landscape-locked
  // roadmap (wide + short, e.g. ~900x520) read as NEITHER small (no sheet) NOR - once
  // a separate min-height:600px native floor was added - tall enough for the bar,
  // leaving a dead zone with NO play affordance at all (MUS-523; Vic's Moto G05). The
  // roadmap is landscape-locked on phones, so that wide-and-short shape is the NORMAL
  // phone shape here, not an edge case. Routing by device class makes the sheet and
  // the bar exact complements of one boolean (`useSheetUi` vs `!useSheetUi`), so the
  // two paths tile the whole plane with no gap and no overlap on every device - the
  // dead-zone class of bug cannot recur. `getDeviceClass()` is the same phone signal
  // already used for phone-specific UI elsewhere (e.g. RepertoirePlay); it is derived
  // from the device/UA, not the viewport, so it does not change on resize/rotate.
  const isPhone = getDeviceClass() === 'phone';
  const isSmallWindow = useMediaQuery(smallMobileRoadmapQuery);
  const useSheetUi = isPhone || isSmallWindow;
  // Downstream (Path, the menu button, the Play gate) consumes this as the single
  // "phone/compact experience" switch. Name kept as isSmallMobile to preserve the
  // Path prop contract; its MEANING is now "use the sheet UI", not "viewport <=480".
  const isSmallMobile = useSheetUi;

  // Desktop-only: prevent trackpad/back-gesture weirdness. Skip on Capacitor.
  useEffect(() => {
    if (isCapacitorNative()) return;

    const preventNavigation = (e: WheelEvent) => {
      if (roadmapRef.current && !cursorInRoadmap) e.preventDefault();
    };

    const el = roadmapRef.current;
    if (el) {
      el.addEventListener('wheel', preventNavigation as EventListener, { passive: false });
      return () => el.removeEventListener('wheel', preventNavigation as EventListener);
    }
  }, [cursorInRoadmap]);

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!latestLevelRef.current) return;
    const boxRect = latestLevelRef.current.getBoundingClientRect();
    const isInside =
      e.clientX >= boxRect.left &&
      e.clientX <= boxRect.right &&
      e.clientY >= boxRect.top &&
      e.clientY <= boxRect.bottom;

    setCursorInRoadmap(isInside);
  };

  const handleUnitSelect = useCallback(
    (unit: number) => {
      dispatch(appActions.setUnitSelect({ unitSelect: unit }));
    },
    [dispatch],
  );

  const selectedLevel = levelData[levelSelect] || levelData[0];
  const level_number = selectedLevel ? selectedLevel.level_number : 0;
  const name = selectedLevel ? selectedLevel.name : '';
  const description = selectedLevel?.description;

  const lastLevelComplete = highestCompleteData.lastLevelComplete;
  const userLevelStatuses = highestCompleteData.userLevelStatuses;
  const userLevelTiersComplete = highestCompleteData.userLevelTiersComplete;

  let selectedLevelProgress: UserLevelData | undefined;
  userLevelData.forEach((ul) => {
    if (ul?.level?.level_number == level_number) selectedLevelProgress = ul;
  });

  const unitPercentComplete = getUnitPctComplete(lastLevelComplete, levelData);
  const tierByLevel = tiersByLevels
    ? tiersByLevels.find((item) => item.level_number === level_number)
    : undefined;
  const tierCountForLevel = tierByLevel?.tiers?.length || 1;

  if (!levelData || levelData.length < 2) {
    return (
      <Box>
        <Box
          sx={{
            width: '100vw',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CustomCircularProgress />
        </Box>
      </Box>
    );
  }

  const isCap = isCapacitorNative();

  return (
    <Box
      sx={{ display: 'flex', height: '100vh', width: '100%' }}
      ref={roadmapRef}
      onPointerMove={handlePointerMove}
    >
      <Menu
        selected={'Roadmap'}
        forceDrawer={useSheetUi}
        mobileOpen={menuOpen}
        setMobileOpen={setMenuOpen}
        hideMobileTrigger
      />
      <Survey />

      {showPaymentConfirmation && <Confirmation setShowConfirmation={setShowPaymentConfirmation} />}

      {debugDummyMidiEnabled && (
        <IconButton
          onClick={() => navigate('/piano-debug')}
          aria-label="Open MIDI keyboard debug"
          sx={{
            position: 'fixed',
            top: '16px',
            right: '16px',
            zIndex: 1001,
            color: '#40E16D',
            backgroundColor: 'rgba(64, 225, 109, 0.1)',
            border: '1px solid rgba(64, 225, 109, 0.46)',
            '&:hover': { backgroundColor: 'rgba(64, 225, 109, 0.2)' },
          }}
        >
          <KeyboardIcon />
        </IconButton>
      )}

      <Box
        sx={{
          width: 'calc(100% - var(--app-sidebar-width, 100px))',
          backgroundColor: 'black',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            background: 'linear-gradient(0deg, black, #1D2F44)',
            height: '100%',
          }}
        >
          <Box className="header-container">
            <Box
              className="roadmap-title-container"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              {isSmallMobile && <MobileMenuButton inline onClick={() => setMenuOpen(true)} />}
              Roadmap
              {!isCap &&
                (process.env.REACT_APP_NODE_ENV === 'local' ||
                  process.env.REACT_APP_NODE_ENV === 'dev') && (
                  <FormControl
                    size="small"
                    sx={{ color: '#fff', svg: { fill: '#fff' }, minWidth: 80 }}
                  >
                    <InputLabel id="unit-select-label" sx={{ color: '#fff' }}>
                      Unit
                    </InputLabel>
                    <Select
                      labelId="unit-select-label"
                      id="unit-select"
                      value={unitSelect.toString()}
                      label="Unit"
                      onChange={(evt: SelectChangeEvent) =>
                        handleUnitSelect(parseInt(evt.target.value))
                      }
                      sx={{ color: '#fff', svg: { fill: '#fff' } }}
                    >
                      <MenuItem value={1}>1</MenuItem>
                      <MenuItem value={2}>2</MenuItem>
                    </Select>
                  </FormControl>
                )}
              {isDevEnvironment && <CopyErrorLogsButton />}
            </Box>
            <Box className="minimap-container">
              <Minimap
                unitPercentComplete={unitPercentComplete}
                scrollPosFromRoadmap={scrollPos}
                setScrollPosFromRoadmap={setScrollPos}
                scrollAreaSize={scrollAreaSize}
                areaToPreventScroll={areaToPreventScroll}
                setAreaToPreventScroll={setAreaToPreventScroll}
                setPathScrollPos={setPathScrollPos}
              />
            </Box>
          </Box>

          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <Box className="roadmap-container-styles">
              <Path
                levelSelect={levelSelect}
                unitSelect={unitSelect}
                levelData={levelData}
                userLevelData={userLevelData}
                lastLevelComplete={lastLevelComplete}
                userLevelStatuses={userLevelStatuses}
                userLevelTiersComplete={userLevelTiersComplete}
                scrollPos={scrollPos}
                setScrollPos={setScrollPos}
                setScrollAreaSize={setScrollAreaSize}
                areaToPreventScroll={areaToPreventScroll}
                setAreaToPreventScroll={setAreaToPreventScroll}
                pathScrollPos={pathScrollPos}
                latestLevelRef={latestLevelRef}
                isSmallMobile={isSmallMobile}
              />
            </Box>

            {/*
              The Play bar renders iff we are NOT using the sheet UI - the exact
              complement of the sheet's render condition (Path.tsx renders the sheet
              iff isSmallMobile === useSheetUi). Because the two gates are literal
              opposites of one boolean, exactly one play affordance renders at every
              viewport on every device: no gap (MUS-523's dead zone) and no overlap.
              Phones -> sheet (this is false); tablets/desktop -> bar (this is true).
            */}
            {!isSmallMobile && (
              <PlayContainer
                selectedLevel={selectedLevel}
                levelSelect={levelSelect}
                level_number={level_number}
                name={name}
                description={description}
                selectedLevelProgress={selectedLevelProgress}
                tierCountForLevel={tierCountForLevel}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Roadmap;
